import React, { useEffect, useRef, useState } from "react";
import { api } from "../api/client";

const ICONS = {
  dae: "❤",
  eglise: "⛪", monument: "🗿", lavoir: "🚰", ruine: "🏛", moulin: "💨", autre: "📍",
  commerce: "🛒", sport: "⚽", tri: "♻", mobilier: "🪑", public: "🏛",
};

const COLORS = {
  dae: "#b54b3a",
  patrimoine: "#8a5cd1",
  commerce: "#e67e22",
  sport: "#27ae60",
  tri: "#16a085",
  mobilier: "#7f8c8d",
  public: "#1d4f91",
};

const CATEGORIES_LABEL = {
  public: "Services publics",
  commerce: "Commerces & services",
  sport: "Sport & aires de jeux",
  tri: "Points d'apport (tri)",
  mobilier: "Mobilier urbain",
};

const ETAT_VOIRIE = {
  degagee:      { label: "Dégagée",      color: "#27ae60" },
  en_cours:     { label: "En cours",     color: "#f39c12" },
  non_traitee:  { label: "Non traitée",  color: "#e74c3c" },
  impraticable: { label: "Impraticable", color: "#2c3e50" },
  inondee:      { label: "Inondée",      color: "#2980b9" },
  inconnue:     { label: "État inconnu", color: "#bdc3c7" },
};

function makeIcon(L, emoji, color) {
  const html = `<div style="background:${color}; width:34px; height:34px; border-radius:50%;
    display:flex; align-items:center; justify-content:center; color:#fff;
    font-size:16px; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.3);">${emoji}</div>`;
  return L.divIcon({ html, className: "", iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -17] });
}

const escapeHtml = (s) => String(s || "").replace(/[&<>"']/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default function Cartographie() {
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const layers = useRef({});

  const [showDAE, setShowDAE] = useState(true);
  const [showPatrimoine, setShowPatrimoine] = useState(true);
  const [showPublic, setShowPublic] = useState(true);
  const [showCommerce, setShowCommerce] = useState(true);
  const [showSport, setShowSport] = useState(true);
  const [showTri, setShowTri] = useState(true);
  const [showMobilier, setShowMobilier] = useState(true);
  const [showTraces, setShowTraces] = useState(true);
  const [showVoiries, setShowVoiries] = useState(true);
  const [showCadastre, setShowCadastre] = useState(false);

  const [dae, setDae] = useState([]);
  const [pat, setPat] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [traces, setTraces] = useState([]);
  const [voiries, setVoiries] = useState([]);
  const [voiriesMaj, setVoiriesMaj] = useState(null);
  const [risques, setRisques] = useState(null);
  const [risquesLoading, setRisquesLoading] = useState(true);

  const lat = 48.1102, lon = 0.0815;

  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current || leafletMap.current) return;

    leafletMap.current = L.map(mapRef.current).setView([lat, lon], 15);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap", maxZoom: 19,
    }).addTo(leafletMap.current);

    layers.current.dae = L.layerGroup().addTo(leafletMap.current);
    layers.current.patrimoine = L.layerGroup().addTo(leafletMap.current);
    layers.current.public = L.layerGroup().addTo(leafletMap.current);
    layers.current.commerce = L.layerGroup().addTo(leafletMap.current);
    layers.current.sport = L.layerGroup();
    layers.current.tri = L.layerGroup();
    layers.current.mobilier = L.layerGroup();
    layers.current.traces = L.layerGroup().addTo(leafletMap.current);
    layers.current.voiries = L.layerGroup().addTo(leafletMap.current);

    layers.current.cadastre = L.tileLayer(
      "https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0" +
      "&LAYER=CADASTRALPARCELS.PARCELLAIRE_EXPRESS&STYLE=normal" +
      "&FORMAT=image/png&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}",
      { attribution: "Cadastre © DGFiP / IGN", maxZoom: 19, opacity: 0.7 }
    );

    return () => {
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
  }, []);

  useEffect(() => {
    (async () => {
      try { setDae((await api.get("/cartographie/dae")).items || []); } catch { setDae([]); }
      try { setPat((await api.get("/cartographie/patrimoine")).items || []); } catch { setPat([]); }
      try { setLieux((await api.get("/cartographie/lieux")).items || []); } catch { setLieux([]); }
      try { setTraces((await api.get("/cartographie/traces")).items || []); } catch { setTraces([]); }
      try {
        const v = await api.get("/cartographie/voiries");
        setVoiries(v.items || []);
        setVoiriesMaj(v.derniere_maj || null);
      } catch { setVoiries([]); setVoiriesMaj(null); }
      try {
        setRisquesLoading(true);
        setRisques(await api.get("/cartographie/risques"));
      } catch { setRisques({ api_error: true }); }
      finally { setRisquesLoading(false); }
    })();
  }, []);

  // DAE
  useEffect(() => {
    const L = window.L; if (!L || !layers.current.dae) return;
    layers.current.dae.clearLayers();
    if (!showDAE) return;
    dae.forEach((d) => {
      L.marker([d.latitude, d.longitude], { icon: makeIcon(L, ICONS.dae, COLORS.dae) })
        .bindPopup(`<div style="min-width:200px;">
          <div style="font-weight:600; color:${COLORS.dae};">❤ Défibrillateur</div>
          <div style="font-weight:500;">${escapeHtml(d.nom)}</div>
          ${d.adresse ? `<div class="muted" style="font-size:0.9em;">${escapeHtml(d.adresse)}</div>` : ""}
          ${d.acces ? `<div style="font-size:0.85em; margin-top:4px;">${escapeHtml(d.acces)}</div>` : ""}
          ${d.horaires ? `<div style="font-size:0.8em; color:#888;">${escapeHtml(d.horaires)}</div>` : ""}
        </div>`).addTo(layers.current.dae);
    });
  }, [showDAE, dae]);

  // Patrimoine
  useEffect(() => {
    const L = window.L; if (!L || !layers.current.patrimoine) return;
    layers.current.patrimoine.clearLayers();
    if (!showPatrimoine) return;
    pat.forEach((p) => {
      L.marker([p.latitude, p.longitude],
              { icon: makeIcon(L, ICONS[p.type] || ICONS.autre, COLORS.patrimoine) })
        .bindPopup(`<div style="min-width:220px;">
          <div style="font-weight:600; color:${COLORS.patrimoine};">${escapeHtml(p.type)}</div>
          <div style="font-weight:500;">${escapeHtml(p.nom)}</div>
          ${p.description ? `<div style="font-size:0.85em; margin-top:6px; line-height:1.4;">${escapeHtml(p.description)}</div>` : ""}
        </div>`).addTo(layers.current.patrimoine);
    });
  }, [showPatrimoine, pat]);

  // Lieux (par catégorie)
  useEffect(() => {
    const L = window.L; if (!L) return;
    const filters = { public: showPublic, commerce: showCommerce, sport: showSport,
                      tri: showTri, mobilier: showMobilier };
    for (const cat of ["public", "commerce", "sport", "tri", "mobilier"]) {
      if (!layers.current[cat]) continue;
      layers.current[cat].clearLayers();
      if (!filters[cat]) {
        if (leafletMap.current.hasLayer(layers.current[cat]))
          leafletMap.current.removeLayer(layers.current[cat]);
        continue;
      }
      if (!leafletMap.current.hasLayer(layers.current[cat]))
        layers.current[cat].addTo(leafletMap.current);
      lieux.filter((l) => l.categorie === cat).forEach((l) => {
        const showSous = l.sous_type &&
          l.sous_type.trim().toLowerCase() !== l.nom.trim().toLowerCase();
        L.marker([l.latitude, l.longitude],
                 { icon: makeIcon(L, ICONS[cat], COLORS[cat]) })
          .bindPopup(`<div style="min-width:220px;">
            <div style="font-weight:600; color:${COLORS[cat]};">${escapeHtml(l.nom)}</div>
            ${showSous ? `<div class="muted" style="font-size:0.8em;">${escapeHtml(l.sous_type)}</div>` : ""}
            ${l.adresse ? `<div style="font-size:0.85em; margin-top:4px;">${escapeHtml(l.adresse)}</div>` : ""}
            ${l.horaires ? `<div style="font-size:0.8em; color:#666;">🕐 ${escapeHtml(l.horaires)}</div>` : ""}
            ${l.telephone ? `<div style="font-size:0.8em;">📞 ${escapeHtml(l.telephone)}</div>` : ""}
            ${l.url ? `<div style="font-size:0.8em;"><a href="${escapeHtml(l.url)}" target="_blank" rel="noreferrer">Site web</a></div>` : ""}
            ${l.description ? `<div style="font-size:0.85em; margin-top:6px;">${escapeHtml(l.description)}</div>` : ""}
          </div>`).addTo(layers.current[cat]);
      });
    }
  }, [showPublic, showCommerce, showSport, showTri, showMobilier, lieux]);

  // Traces (polylignes)
  useEffect(() => {
    const L = window.L; if (!L || !layers.current.traces) return;
    layers.current.traces.clearLayers();
    if (!showTraces) return;
    traces.forEach((t) => {
      if (!t.geojson) return;
      const layer = L.geoJSON(t.geojson, {
        style: { color: t.couleur || "#d9a521", weight: 4, opacity: 0.85 },
      });
      const dist = t.distance_km ? `${t.distance_km} km` : "";
      const popup = `<div style="min-width:220px;">
        <div style="font-weight:600; color:${t.couleur || "#d9a521"};">${escapeHtml(t.type)}</div>
        <div style="font-weight:500;">${escapeHtml(t.nom)}</div>
        ${dist ? `<div class="muted">${dist}${t.difficulte ? " · " + escapeHtml(t.difficulte) : ""}</div>` : ""}
        ${t.description ? `<div style="font-size:0.85em; margin-top:6px;">${escapeHtml(t.description)}</div>` : ""}
      </div>`;
      layer.bindPopup(popup);
      layer.addTo(layers.current.traces);
    });
  }, [showTraces, traces]);

  // Voiries (déneigement / inondation)
  useEffect(() => {
    const L = window.L; if (!L || !layers.current.voiries) return;
    layers.current.voiries.clearLayers();
    if (!showVoiries) return;
    // Sur la carte publique : on ne montre QUE les voies à signaler
    // (en cours, non traitée, impraticable, inondée). Les voies dégagées
    // ou d'état inconnu n'apportent pas d'info au citoyen.
    const ETATS_AFFICHES = new Set(["en_cours", "non_traitee", "impraticable", "inondee"]);
    voiries.forEach((v) => {
      if (!v.geojson) return;
      if (!ETATS_AFFICHES.has(v.etat)) return;
      const meta = ETAT_VOIRIE[v.etat] || ETAT_VOIRIE.inconnue;
      const layer = L.geoJSON(v.geojson, {
        style: { color: meta.color, weight: 6, opacity: 0.85 },
      });
      const maj = v.etat_maj_at
        ? new Date(v.etat_maj_at).toLocaleString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          })
        : null;
      const popup = `<div style="min-width:220px;">
        <div style="font-weight:600; color:${meta.color};">${escapeHtml(meta.label)}</div>
        <div style="font-weight:500;">${escapeHtml(v.nom)}</div>
        ${v.commentaire ? `<div style="font-size:0.85em; margin-top:6px;">${escapeHtml(v.commentaire)}</div>` : ""}
        ${maj ? `<div class="muted" style="font-size:0.75em; margin-top:6px;">Mis à jour le ${maj}</div>` : ""}
      </div>`;
      layer.bindPopup(popup);
      layer.addTo(layers.current.voiries);
    });
  }, [showVoiries, voiries]);
  useEffect(() => {
    if (!leafletMap.current || !layers.current.cadastre) return;
    if (showCadastre) layers.current.cadastre.addTo(leafletMap.current);
    else layers.current.cadastre.remove();
  }, [showCadastre]);

  const nb = {
    dae: dae.length,
    pat: pat.length,
    pub: lieux.filter((l) => l.categorie === "public").length,
    commerce: lieux.filter((l) => l.categorie === "commerce").length,
    sport: lieux.filter((l) => l.categorie === "sport").length,
    tri: lieux.filter((l) => l.categorie === "tri").length,
    mobilier: lieux.filter((l) => l.categorie === "mobilier").length,
    traces: traces.length,
  };

  return (
    <div className="container">
      <h1>Cartographie & données publiques</h1>
      <p className="muted mb-3">
        Carte interactive de la commune. Activez ou désactivez les couches
        ci-dessous pour afficher les défibrillateurs, le patrimoine, les
        commerces, les sentiers, le cadastre, etc.
      </p>

      <div className="carto-layout">
        <div className="carto-side">
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Points</h3>
            <Toggle on={showDAE} set={setShowDAE} color={COLORS.dae} icon={ICONS.dae}
                    label={`Défibrillateurs (${nb.dae})`} />
            <Toggle on={showPatrimoine} set={setShowPatrimoine} color={COLORS.patrimoine} icon="⛪"
                    label={`Patrimoine (${nb.pat})`} />
            <Toggle on={showPublic} set={setShowPublic} color={COLORS.public} icon={ICONS.public}
                    label={`Services publics (${nb.pub})`} />
            <Toggle on={showCommerce} set={setShowCommerce} color={COLORS.commerce} icon={ICONS.commerce}
                    label={`Commerces & services (${nb.commerce})`} />
            <Toggle on={showSport} set={setShowSport} color={COLORS.sport} icon={ICONS.sport}
                    label={`Sport & jeux (${nb.sport})`} />
            <Toggle on={showTri} set={setShowTri} color={COLORS.tri} icon={ICONS.tri}
                    label={`Apport volontaire (${nb.tri})`} />
            <Toggle on={showMobilier} set={setShowMobilier} color={COLORS.mobilier} icon={ICONS.mobilier}
                    label={`Mobilier urbain (${nb.mobilier})`} />
          </div>

          <div className="card mt-2">
            <h3 style={{ marginTop: 0 }}>Tracés</h3>
            <Toggle on={showTraces} set={setShowTraces} color="#d9a521" icon="🥾"
                    label={`Sentiers (${nb.traces})`} />
            <Toggle on={showVoiries} set={setShowVoiries} color="#2980b9" icon="❄"
                    label={`Voiries à signaler (${voiries.filter(v => ["en_cours","non_traitee","impraticable","inondee"].includes(v.etat)).length})`} />
            {showVoiries && voiries.length > 0 && (
              <div style={{ marginTop: "0.6rem", paddingTop: "0.5rem",
                            borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                <div className="muted" style={{ fontSize: "0.75em", marginBottom: 4 }}>
                  Légende état :
                </div>
                {Object.entries(ETAT_VOIRIE)
                  .filter(([k]) => ["en_cours","non_traitee","impraticable","inondee"].includes(k))
                  .map(([k, v]) => (
                  <div key={k} style={{ display: "flex", alignItems: "center",
                                        gap: 6, fontSize: "0.78em", padding: "1px 0" }}>
                    <span style={{ width: 18, height: 4, background: v.color,
                                   borderRadius: 2 }} />
                    <span>{v.label}</span>
                  </div>
                ))}
                {voiriesMaj && (
                  <div className="muted" style={{ fontSize: "0.72em", marginTop: 6 }}>
                    Dernière mise à jour :{" "}
                    {new Date(voiriesMaj).toLocaleString("fr-FR", {
                      day: "2-digit", month: "2-digit",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card mt-2">
            <h3 style={{ marginTop: 0 }}>Cartes superposées</h3>
            <Toggle on={showCadastre} set={setShowCadastre} color="#1d4f91" icon="▦"
                    label="Cadastre" />
            <p className="muted" style={{ fontSize: "0.75em", marginTop: "0.5rem" }}>
              Les parcelles cadastrales n'apparaissent qu'à fort niveau de zoom.
            </p>
          </div>

          <RisquesPanel risques={risques} loading={risquesLoading} />
        </div>

        <div className="carto-map-wrap">
          <div ref={mapRef} className="carto-map" />
        </div>
      </div>
    </div>
  );
}

function Toggle({ on, set, color, icon, label }) {
  return (
    <label className="carto-toggle">
      <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} />
      <span className="carto-pin" style={{ background: color }}>{icon}</span>
      <span>{label}</span>
    </label>
  );
}

function RisquesPanel({ risques, loading }) {
  if (loading) return (
    <div className="card mt-2"><h3 style={{ marginTop: 0 }}>Risques</h3>
      <p className="muted">Chargement…</p></div>);
  if (!risques || risques.api_error) return (
    <div className="card mt-2"><h3 style={{ marginTop: 0 }}>Risques</h3>
      <p className="muted">Données indisponibles. Voir{" "}
        <a href="https://www.georisques.gouv.fr" target="_blank" rel="noreferrer">
          Géorisques.gouv.fr</a>.</p></div>);
  const hasData = risques.argile || risques.sismicite || risques.radon
                  || risques.catnat_count > 0;
  return (
    <div className="card mt-2">
      <h3 style={{ marginTop: 0 }}>Risques sur la commune</h3>
      <p className="muted" style={{ fontSize: "0.8em" }}>
        BRGM / Géorisques ({risques.code_insee}).
      </p>
      {!hasData && <p className="muted">Aucun risque majeur référencé.</p>}
      <div className="risques-list">
        {risques.argile && <RisqueLine label="Argiles" val={risques.argile} />}
        {risques.sismicite && <RisqueLine label="Zone sismique" val={risques.sismicite} />}
        {risques.radon && <RisqueLine label="Potentiel radon" val={risques.radon} />}
        {risques.catnat_count > 0 &&
          <RisqueLine label="Arrêtés Cat-Nat" val={risques.catnat_count} />}
      </div>
      <p className="muted" style={{ fontSize: "0.75em", marginTop: "0.6rem" }}>
        Analyse par adresse :{" "}
        <a href="https://errial.georisques.gouv.fr" target="_blank" rel="noreferrer">
          errial.georisques.gouv.fr</a>
      </p>
    </div>
  );
}

function RisqueLine({ label, val }) {
  return (
    <div className="risque-item">
      <span className="risque-label">{label}</span>
      <span className="risque-val">{val}</span>
    </div>
  );
}
